import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  Box,
  Chip,
} from '@mui/material';
import { Calendar, Clock } from 'lucide-react';
import { Patient } from '../../types';
import { useActiveDoctors, useCreateAppointment } from '../../hooks/useQueries';

interface FollowUpModalProps {
  open: boolean;
  onClose: () => void;
  patient: Patient;
  currentDoctorId?: string;
  onFollowUpScheduled?: () => void;
}

export function FollowUpModal({ 
  open, 
  onClose, 
  patient, 
  currentDoctorId,
  onFollowUpScheduled 
}: FollowUpModalProps) {
  const [doctorId, setDoctorId] = useState(currentDoctorId || '');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('10:00');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Use React Query hooks
  const { data: doctors = [] } = useActiveDoctors();
  const createAppointmentMutation = useCreateAppointment();

  // Load doctors on modal open
  useEffect(() => {
    if (open) {
      // Set default date to tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setFollowUpDate(tomorrow.toISOString().split('T')[0]);
      
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (currentDoctorId && doctors.length > 0) {
      setDoctorId(currentDoctorId);
    } else if (!doctorId && doctors.length > 0) {
      setDoctorId(doctors[0].id);
    }
  }, [currentDoctorId, doctors, doctorId]);

  const handleSubmit = async () => {
    try {
      setError(null);

      // Validation
      if (!doctorId) {
        throw new Error('Please select a doctor');
      }
      if (!followUpDate) {
        throw new Error('Follow-up date is required');
      }

      // Check if date is not in the past
      const selectedDate = new Date(followUpDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        throw new Error('Follow-up date cannot be in the past');
      }

      // Create follow-up appointment using React Query mutation
      await createAppointmentMutation.mutateAsync({
        doctorId: doctorId,
        visitDate: followUpDate,
        visitType: 'FOLLOWUP',
        patientName: patient.name,
        mobile: patient.mobile,
        age: patient.age ?? 0, // Default to 0 if age is undefined
        gender: patient.gender,
        address: patient.address || undefined,
        notes: notes ? `Follow-up visit. ${notes}` : 'Follow-up visit',
      });

      // Close after showing success
      setTimeout(() => {
        if (onFollowUpScheduled) {
          onFollowUpScheduled();
        }
        onClose();
        // Reset form
        setNotes('');
        setError(null);
      }, 1000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to schedule follow-up';
      setError(message);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1.3rem', fontWeight: 700, pb: 1 }}>
        Schedule Follow-Up Appointment
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, paddingTop: 2, padding: { xs: 2, sm: 3 } }}>
        {error && <Alert severity="error">{error}</Alert>}
        {createAppointmentMutation.isSuccess && (
          <Alert severity="success">Follow-up appointment scheduled successfully!</Alert>
        )}

        {/* Patient Info */}
        <Box sx={{ 
          p: 2, 
          borderRadius: 2, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1f2937' : '#f9fafb',
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'dark' ? '#374151' : '#e5e7eb'
        }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">Patient</Typography>
            <Typography variant="h6" fontWeight={700}>{patient.name}</Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={`${patient.age || 'N/A'} years`} size="small" />
              <Chip label={patient.gender} size="small" />
              <Chip label={patient.mobile} size="small" variant="outlined" />
            </Stack>
          </Stack>
        </Box>

        {/* Doctor Selection */}
        <FormControl fullWidth>
          <InputLabel>Select Doctor</InputLabel>
          <Select
            value={doctorId}
            onChange={(e) => setDoctorId(e.target.value)}
            label="Select Doctor"
          >
            {doctors.map((doctor) => (
              <MenuItem key={doctor.id} value={doctor.id}>
                Dr. {doctor.name} - {doctor.specialization || 'General'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Follow-up Date */}
        <TextField
          fullWidth
          type="date"
          label="Follow-Up Date"
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: getMinDate() }}
          InputProps={{
            startAdornment: <Calendar size={18} style={{ marginRight: 8, opacity: 0.6 }} />,
          }}
          helperText="Select a future date for the follow-up visit"
        />

        {/* Preferred Time */}
        <FormControl fullWidth>
          <InputLabel>Preferred Time</InputLabel>
          <Select
            value={followUpTime}
            onChange={(e) => setFollowUpTime(e.target.value)}
            label="Preferred Time"
            startAdornment={<Clock size={18} style={{ marginLeft: 8, marginRight: 4, opacity: 0.6 }} />}
          >
            {timeSlots.map((time) => (
              <MenuItem key={time} value={time}>
                {time}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Notes */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Notes (Optional)"
          placeholder="Any specific instructions or reason for follow-up..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
          This will create a new appointment for <strong>{patient.name}</strong> marked as a follow-up visit.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={createAppointmentMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={createAppointmentMutation.isPending || !doctorId || !followUpDate}
          startIcon={createAppointmentMutation.isPending ? <CircularProgress size={20} /> : <Calendar size={18} />}
        >
          {createAppointmentMutation.isPending ? 'Scheduling...' : 'Schedule Follow-Up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
